import { orchestrator } from 'satcheljs';
import { getBrowserWidth } from 'owa-config';
import {
    onCopilotChatDrawerOpened,
    onCopilotChatDrawerCollapsedState,
    onCopilotChatDrawerExpandedState,
    onCopilotChatDrawerExpandRequested,
    COLLAPSE_FOLDER_PANE_WHEN_CHAT_OPENED_BREAKPOINT,
    shouldChatCollapseFolderPane,
    getShowBizChatInReadingPane,
    setShowBizChatInReadingPaneMutator,
    setDisableEmptyReadingPaneFillMutator,
} from 'owa-copilot-chat-drawer-store';
import { type StateChangeSource } from 'owa-left-pane-layout/lib/types/StateChangeSource';
import {
    getLastCollapsedFolderPaneStateChangeSource,
    getLastExpandedFolderPaneStateChangeSource,
} from 'owa-left-pane-layout/lib/utils/folderPaneStateTracking';
import { setShowFolderPane } from '../actions/setShowFolderPane';
import { shouldShowFolderPane } from '../selectors/shouldShowFolderPane';
import { shouldDockBizChatInEmptyReadingPane } from '../utils/shouldDockBizChatInEmptyReadingPane';

/**
 * On chat drawer open: either dock into an empty reading pane (mutually exclusive with
 * folder-pane collapse), or optionally collapse the folder pane to give chat more room.
 * The dock decision must be synchronous so the FP-collapse guard sees up-to-date state.
 */
orchestrator(onCopilotChatDrawerOpened, actionMessage => {
    const { targetWindow } = actionMessage;

    if (shouldDockBizChatInEmptyReadingPane(targetWindow)) {
        setShowBizChatInReadingPaneMutator(targetWindow, true);
        return;
    }

    if (
        targetWindow === window && // only affect main window
        getBrowserWidth() < COLLAPSE_FOLDER_PANE_WHEN_CHAT_OPENED_BREAKPOINT &&
        shouldShowFolderPane() && // if FP is shown
        !getShowBizChatInReadingPane(targetWindow) // don't collapse FP if chat is in reading pane
    ) {
        setShowFolderPane(false /* showFolderPane */, 9);
    }
});

/**
 * When the user clicks Expand, treat it as a re-opt-in to the empty-RP fill — clear
 * the session-scoped opt-out and fill if conditions match. Without this, a user who
 * previously collapsed could only return to RP-mode by closing and reopening chat.
 */
orchestrator(onCopilotChatDrawerExpandRequested, ({ targetWindow }) => {
    setDisableEmptyReadingPaneFillMutator(targetWindow, false);
    if (shouldDockBizChatInEmptyReadingPane(targetWindow)) {
        setShowBizChatInReadingPaneMutator(targetWindow, true);
    }
});

/**
 * Orchestrator for when Copilot Chat Drawer is collapsed to minimum width.
 *
 * Used here as a signal to determine whether we should reshow folder pane for the user.
 * We only reshow if the last time folder pane collapsed was due to chat opening.
 */
orchestrator(onCopilotChatDrawerCollapsedState, actionMessage => {
    const { targetWindow } = actionMessage;
    if (
        targetWindow === window && // only affect main window
        getLastCollapsedFolderPaneStateChangeSource() === 9 &&
        !shouldShowFolderPane() // if FP is currently hidden
    ) {
        setShowFolderPane(true /* showFolderPane */, 10);
    }
});

/**
 * Orchestrator for when Copilot Chat Drawer is expanded from minimum width.
 *
 * Used here as a signal to determine whether we should re-hide folder pane for the user.
 * We only re-hide if the last time folder pane expanded was due to chat being in collapsed state.
 */
orchestrator(onCopilotChatDrawerExpandedState, actionMessage => {
    const { targetWindow } = actionMessage;
    if (
        targetWindow === window && // only affect main window
        getLastExpandedFolderPaneStateChangeSource() === 10 &&
        shouldShowFolderPane() && // if FP is currently shown
        shouldChatCollapseFolderPane()
    ) {
        setShowFolderPane(false /* showFolderPane */, 9);
    }
});
